import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { type ComponentRef, type FC, type RefObject } from 'react'

import { Dialog, Text, VStack } from '@/components'

dayjs.extend(relativeTime)

export interface SparkConfirmDialogProps {
  balance: number
  confirmTestID?: string
  finalFocusRef?: RefObject<ComponentRef<any> | null>
  isLoading?: boolean
  isOpen: boolean
  /** currentUser.nextSparkRefillAt — null for non-D+ users, who never refill */
  nextRefillAt?: string | null
  onClose: () => void
  onConfirm: () => void | Promise<void>
  onGetMoreSparks: () => void
  profileName?: string | null
}

export const SparkConfirmDialog: FC<SparkConfirmDialogProps> = ({
  balance,
  confirmTestID,
  finalFocusRef,
  isLoading,
  isOpen,
  nextRefillAt,
  onClose,
  onConfirm,
  onGetMoreSparks,
  profileName
}) => {
  const hasSparks = balance > 0
  const name = profileName ?? 'this person'

  return (
    <Dialog
      confirmLabel={hasSparks ? 'Confirm' : 'Get More Sparks'}
      confirmTestID={confirmTestID}
      finalFocusRef={finalFocusRef}
      isLoading={isLoading}
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={hasSparks ? onConfirm : onGetMoreSparks}
      title={`Send ${name} a Spark`}
    >
      <VStack className='gap-4'>
        <Text className='text-sm text-white'>{`Send a spark to shoot to the top of ${name}'s Likes List`}</Text>
        <Text className='text-sm text-white'>{`You will appear unlocked regardless of the user's subscription status.`}</Text>
        <Text className='text-sm text-white'>
          {hasSparks ? `Sparks remaining: ${balance}` : `You have no Sparks remaining.`}
        </Text>
        {!hasSparks && nextRefillAt && dayjs(nextRefillAt).isAfter(dayjs()) ? (
          // A D+ subscriber's weekly grant refills on a schedule; without this line the
          // out-of-sparks state reads as purchase-only for something they get free shortly.
          // Future-only: a cached refill time that has already passed means the balance is
          // stale too — "refill 3 hours ago" next to a zero balance would be nonsense.
          <Text className='text-sm text-white'>{`Your free weekly Sparks refill ${dayjs(nextRefillAt).fromNow()}.`}</Text>
        ) : null}
      </VStack>
    </Dialog>
  )
}
